import { forwardRef, Suspense, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion, useTransform } from "framer-motion"
import { Global } from "@emotion/react"
import styled from "@emotion/styled"
import FocusTrap from "focus-trap-react"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useSuspender } from "contexts/Suspender"

import getMediaQuery, { media } from "css/breakpoints"
import { buttonOffset } from "css/buttons"
import { useViewport } from "contexts/Viewport"
import useScrollLock from "hooks/useScrollLock"

import Button from "components/button/Button"
import Link from "components/link/Link"
import Image from "components/media/ImageCLD"
import { colors } from "components/model/colors"
import { H1, HGroup, ShortSpecs, Title } from "components/model/model-majesty/style"
import WishlistButton from "components/wishlist-button/WishlistButton"
import FigCaption, { horizontalAlign, innerStyle2 } from "components/figcaption/FigCaption"

import Arrows from "./Arrows"
import BeautyshotCardItem from "./BeautyshotCardItem"
import { RollerProvider, useRoller } from "./contexts"
import Markers from "./Markers"

const Push = styled.div()

const Close = styled(Button)`
  /* --fill: #fff; */
  --spacing: calc(var(--outer-margin) - var(--height) / 2);
  ${getMediaQuery("m")} {
    --spacing: calc(var(--outer-margin) - var(--grid-gap) / 2 - var(--height) / 2);
  }
  ${getMediaQuery("l")} {
    --spacing: calc((var(--outer-margin) - var(--grid-gap)) / 2 - var(--height) / 2);
  }

  inset-block-start: var(--spacing);
  inset-inline-end: var(--spacing);

  position: fixed;
  z-index: 1;
  & svg {
    fill: var(--fill) !important;
  }
`

const Underlay = styled(motion.div)`
  inset: 0;
  position: fixed;
  z-index: calc(var(--z-top, 10000));
`

const Modal = styled(motion.div)`
  background: linear-gradient(to top, #f0f0f0, #fff);
  height: 100vh;
  width: 100vw;
`

const Discover = styled(Link)`
  ${buttonOffset}
  margin-block-start: -0.5rem;
`

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  ::-webkit-scrollbar {
    display: none;
  }
`

const WBContainer = styled.div`
  position: absolute;
  top: 2rem;

  button {
    // to override default button style in that case
    color: rgba(var(--green)) !important;

    svg {
      fill: rgba(var(--green)) !important;
    }

    &:hover {
      color: rgba(var(--light-black)) !important;

      svg {
        fill: rgba(var(--light-black)) !important;
      }
    }
  }
`

const MajestyStyled = styled.div`
  padding-block: 3.125rem 3.125rem;
  position: relative;
  pointer-events: none;

  a,
  button {
    pointer-events: auto;

    html.prefers-contrast & {
      margin-block-start: 0.5rem;
    }
  }

  ${getMediaQuery("s", { max: true })} {
    margin-block-start: -20%;
  }
`

const Figure = styled.figure`
  display: grid;
  margin-block-end: -10%;
  pointer-events: none;

  > * {
    grid-area: 1 / -1;
  }

  img {
    height: 60vh;
    object-fit: contain;
    pointer-events: none;
  }
`

const BeautyFigure = styled.figure`
  position: relative;
  display: grid;

  & picture,
  & img {
    grid-column: 1;
    grid-row: 1;
    overflow: hidden;
  }

  & figcaption {
    ${innerStyle2};
    margin-block-end: calc(2.5rem + 30px);

    ${getMediaQuery("m")} {
      margin-block-end: 3em;
    }

    &.${horizontalAlign.start} {
      margin-inline-start: 3em;
    }
    &.${horizontalAlign.end} {
      margin-inline-end: 3em;
    }
  }

  /* & figcaption {
    height: fit-content;
  } */
`

export function BootstrapModel({ rmc, version = "v01", imageProps, fig_caption, layoutAlt, ...props }) {
  const console = useConsole()
  const locale = useLocale()
  const env = useEnv()
  const scrollLock = useScrollLock()

  const [hasScroll, setHasScroll] = useState(false)

  const rroot = useRef()

  const [[err, model], modelSSR] = useSuspender(
    ["model", locale.current.codes.ws, rmc],
    () =>
      Promise.all([
        Promise.resolve(
          new URL(`/api/catalog/watches/${rmc}?language=${locale.current.codes.ws}`, process.browser ? env.services : process.env.SERVICE_CATALOG_URL)
        )
          .then(url => new Request(url))
          .then(request => fetch(request))
          .then(response => response.json())
          .then(data => {
            if (data.rmc === rmc) return data
            throw { status: 404 }
          }),
      ])
        .then(([model]) => {
          console.verbose("BeautyshotCard Model", model)
          return [null, Object.assign(model)]
        })
        .catch(err => {
          return [err, null]
        }),
    true
  )

  if (err) {
    if (!process.browser) throw err
  }

  useLayoutEffect(() => {
    const r = rroot.current
    scrollLock.lock(r)
    return () => scrollLock.unlock(r)
  })

  useLayoutEffect(() => {
    const r = rroot.current

    const resizeObserver = new ResizeObserver(() => setHasScroll(r.scrollWidth > r.clientWidth))
    resizeObserver.observe(r)

    return () => resizeObserver.unobserve(r)
  }, [])

  console.verbose("BootstrapModel(%o)", { model })
  return (
    <Root ref={rroot}>
      <BeautyshotCardItem layoutAlt={layoutAlt} index={0}>
        <BeautyFigure>
          <Image {...imageProps} sizes={layoutAlt ? "50vw" : "100vw"} loading='lazy' />
          {fig_caption?.caption?.length ? <FigCaption {...fig_caption} /> : null}
        </BeautyFigure>
      </BeautyshotCardItem>
      <BeautyshotCardItem layoutAlt={layoutAlt} index={1}>
        {modelSSR}
        <Majesty model={model} {...props} />
      </BeautyshotCardItem>
      {hasScroll && (
        <>
          <Markers>{["a", "b"]}</Markers>
          <Arrows />
        </>
      )}
    </Root>
  )
}

function Majesty({ model }) {
  const dictionary = useDictionary()
  const { catalogYear, catalogueNewPath } = useEnv()

  const {
    name,
    rmc,
    newmodelselection,
    familyNode: { code: family },
    case: {
      labels: { title: short_specs },
    },
  } = model

  const image = {
    loading: "lazy",
    sizes: "(min-width: 48rem) 42vw, 85vw",
    sources: [
      {
        height: "1380",
        public_id: `${catalogueNewPath}/${catalogYear}/upright-bba-with-shadow/${rmc}`,
        width: "800",
        policy: "t_v7-majesty",
      },
    ],
  }

  // const shadow = {
  //   loading: "lazy",
  //   sizes: "(min-width: 48rem) 42vw, 85vw",
  //   sources: [
  //     {
  //       height: "1380",
  //       public_id: `${catalogueNewPath}/${catalogYear}/upright-c-shadow/${rmc}`,
  //       width: "800",
  //       policy: "t_v7-majesty-shadow",
  //     },
  //   ],
  // }

  return (
    <MajestyStyled>
      <WBContainer>
        <WishlistButton rmc={rmc} family={model.familyNode?.code} modelName={name} newmodelselection={newmodelselection} withLabel />
      </WBContainer>
      <Figure>
        <Image {...image} />
      </Figure>
      <HGroup>
        <H1 as='h2'>
          <Title id='beautyshot-modal-heading'>{name}</Title>
          <ShortSpecs id='beautyshot-modal-content'>{short_specs}</ShortSpecs>
        </H1>
      </HGroup>
      <Discover href={`/watches/${family}/${rmc}`} className='text green' aria_describedby='beautyshot-modal-heading'>
        {dictionary.discoverModel()}
      </Discover>
    </MajestyStyled>
  )
}

function ModalContent({ onClose, rmc, version = "v01", popin_image_cld, popin_image, ...props }) {
  const console = useConsole()
  const dictionary = useDictionary()
  const { isMobile } = useViewport()
  const { rlxcomNewPath } = useEnv()

  const { currentIndex } = useRoller()

  const beautyshotImage = {
    alt: "",
    loading: "lazy",
    sizes: "100vw",
    sources: [
      {
        height: "2160",
        public_id: `${rlxcomNewPath}/collection/watches-grid/popin-cards/${rmc}/${rmc}_${version}`,
        width: "3840",
      },
      {
        height: "2600",
        public_id: `${rlxcomNewPath}/collection/watches-grid/popin-cards/${rmc}/${rmc}_${version}-portrait`,
        width: "1560",
        metadata: {
          v7_type: ["portrait"],
        },
      },
    ],
  }

  const normalizedPopinImage =
    Array.isArray(popin_image || popin_image_cld?.sources) && (popin_image?.length > 0 || popin_image_cld?.sources?.length > 0)
      ? {
          alt: "",
          loading: "lazy",
          sizes: "100vw",
          sources: popin_image || popin_image_cld?.sources,
        }
      : null

  const imageProps = normalizedPopinImage || beautyshotImage

  const desktopImage = imageProps?.sources.find(source => !source?.metadata?.v7_type?.includes("portrait"))
  const layoutAlt = desktopImage?.height / desktopImage?.width >= 1

  const closeFillColor = useTransform([currentIndex, isMobile], ([i, m]) =>
    layoutAlt && !m ? "rgb(var(--light-black))" : i === 0 ? "rgb(var(--pure-white))" : "rgb(var(--light-black))"
  )

  return (
    <Modal
      style={{ "--fill": closeFillColor }}
      aria-modal='true'
      role='dialog'
      aria-labelledby='beautyshot-modal-heading'
      aria-describedby='beautyshot-modal-content'
    >
      <Close icon='close' className='icon translucent-light' onPress={onClose}>
        <span>{dictionary.popinClose()}</span>
      </Close>{" "}
      <Suspense>
        <BootstrapModel rmc={rmc} version={version} layoutAlt={layoutAlt} imageProps={imageProps} {...props} />
      </Suspense>
    </Modal>
  )
}

function BeautyshotCardPush({ onClick, index, isRollerGallery, ...props }, openButtonRef) {
  const console = useConsole()
  const dictionary = useDictionary()

  const bodyElement = process.browser && document.body
  const [isOpen, setIsOpen] = useState(false)

  const { theme, rmc } = props

  function onClose() {
    setIsOpen(false)
  }

  function onOpen(e) {
    setIsOpen(true)
    onClick?.(e)
  }

  console.verbose("BeautyshotCardPush(%o)", props)

  return (
    <>
      <Global styles={colors} />
      <Push>
        {props.children}
        <Button
          ref={openButtonRef}
          aria_label={`${dictionary.discoverMore()} - ${rmc}`}
          icon='plus'
          className={`icon beautyshotcard-open translucent-dark ${theme?.theme ? theme.theme : ""}`}
          onPress={onOpen}
        >
          <span>{props.label}</span>
        </Button>
      </Push>

      {process.browser &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <RollerProvider>
                <FocusTrap focusTrapOptions={{ onDeactivate: onClose }}>
                  <Underlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                    <ModalContent {...props} onClose={onClose} />
                  </Underlay>
                </FocusTrap>
              </RollerProvider>
            )}
          </AnimatePresence>,
          bodyElement
        )}
    </>
  )
}

export default forwardRef(BeautyshotCardPush)
